import {DatePicker, Form, Select} from 'antd';
import {DefaultOptionType} from 'antd/lib/select';
import {Dayjs} from 'dayjs';
import {NoUndefinedRangeValueType} from 'rc-picker/lib/PickerInput/RangePicker';
import {type ReactNode, useCallback} from 'react';

import {FormActiveMark} from '../../../../../../component/form-active-mark/form-active-mark';
import {FormItemRadio} from '../../../../../../component/form-item-radio/form-item-radio';
import {SelectWithCheckboxes} from '../../../../../../component/select-with-checkboxes/select-with-checkboxes';
import {Locale} from '../../../../../../provider/locale/locale';
import {LocaleContextType} from '../../../../../../provider/locale/locale-context-type';
import {ReviewAppType} from '../../../../../../service/reviews/reviews-apps';
import {ReviewDoctorType} from '../../../../../../service/reviews/reviews-doctors';
import {ReviewStatusType} from '../../../../../../service/reviews/reviews-statuses';
import {handleFilterOption} from '../../../../../../util/antd/select-helper';
import * as styles from '../reviews-filter.scss';
import {
    CONTENT_OF_REVIEWS_OPTIONS,
    POSSIBILITY_OF_AN_ANSWER_OPTIONS,
    REPLIES_TO_REVIEWS_OPTIONS,
} from '../reviews-filter-const';
import {reviewsFilterDropdownStyle, reviewsFilterGetPopupContainer} from '../reviews-filter-popup-helper';
import {
    ContentOfReviewsEnum,
    DoctorsReviewsEnum,
    PossibilityOfAnAnswerEnum,
    RepliesToReviewsEnum,
    ReviewsFilterDataType,
    ReviewsFilterFieldNameEnum,
} from '../reviews-filter-type';

import {ReviewsFilterPeriodPickerPanel} from './reviews-filter-period-picker-panel';

const {Option} = Select;

type PropsType = {
    form: ReturnType<typeof Form.useForm>[0];
    reviewFilterState: ReviewsFilterDataType;
    sourcesOptions: Array<{value: number; label: string}>;
    reviewsCatalogListIsInProgress: boolean;
    hasDoctors: boolean | null | undefined;
    reviewsDoctorList: {results?: Array<ReviewDoctorType>} | undefined;
    reviewsDoctorListIsInProgress: boolean;
    showAppFilter: boolean;
    reviewsAppList: {results?: Array<ReviewAppType>} | undefined;
    reviewsAppListIsInProgress: boolean;
    reviewStatusList: {statuses?: Array<ReviewStatusType>} | undefined;
    isReviewStatusListInProgress: boolean;
    periodPickerValue: NoUndefinedRangeValueType<Dayjs>;
    periodPickerActiveTab: string;
    getLocalizedString: LocaleContextType['getLocalizedString'];
    onSourceListChange: (value: Array<number>) => void;
    onDoctorsChange: (value: Array<string>) => void;
    onLocationIdsChange: (value: Array<string>) => void;
    onStatusListChange: (value: Array<string>, options: DefaultOptionType | Array<DefaultOptionType>) => void;
    onDataRangeChange: (date: NoUndefinedRangeValueType<Dayjs> | null) => void;
    onCollectionDateRangeChange: (date: NoUndefinedRangeValueType<Dayjs> | null) => void;
    onPeriodCalendarChange: (dates: NoUndefinedRangeValueType<Dayjs> | null) => void;
    onPeriodPickerTabChange: (activeKey: string) => void;
    onRepliesToReviewsChange: (value: RepliesToReviewsEnum) => void;
    onContentOfReviewsChange: (value: ContentOfReviewsEnum) => void;
    onPossibilityOfAnswerChange: (value: PossibilityOfAnAnswerEnum) => void;
};

export function ReviewsFilterSectionMain(props: PropsType): JSX.Element {
    const {
        form,
        reviewFilterState,
        sourcesOptions,
        reviewsCatalogListIsInProgress,
        hasDoctors,
        reviewsDoctorList,
        reviewsDoctorListIsInProgress,
        showAppFilter,
        reviewsAppList,
        reviewsAppListIsInProgress,
        reviewStatusList,
        isReviewStatusListInProgress,
        periodPickerValue,
        periodPickerActiveTab,
        getLocalizedString,
        onSourceListChange,
        onDoctorsChange,
        onLocationIdsChange,
        onStatusListChange,
        onDataRangeChange,
        onCollectionDateRangeChange,
        onPeriodCalendarChange,
        onPeriodPickerTabChange,
        onRepliesToReviewsChange,
        onContentOfReviewsChange,
        onPossibilityOfAnswerChange,
    } = props;

    const renderPeriodPickerPanel = useCallback(
        (origin: ReactNode) => (
            <ReviewsFilterPeriodPickerPanel
                onPeriodPickerTabChange={onPeriodPickerTabChange}
                origin={origin}
                periodPickerActiveTab={periodPickerActiveTab}
            />
        ),
        [periodPickerActiveTab, onPeriodPickerTabChange]
    );

    return (
        <>
            <div className={styles.ReviewsFilter_selectFilter}>
                <FormActiveMark isVisible={reviewFilterState[ReviewsFilterFieldNameEnum.sourceList].length > 0} />
                <Form.Item
                    label={<Locale stringKey="REVIEWS__FILTER__LABEL__SOURCE" />}
                    name={ReviewsFilterFieldNameEnum.sourceList}
                >
                    <SelectWithCheckboxes<number>
                        disabled={reviewsCatalogListIsInProgress}
                        dropdownStyle={{
                            overflowY: 'auto',
                            zIndex: 9999,
                        }}
                        initialValue={reviewFilterState[ReviewsFilterFieldNameEnum.sourceList]}
                        loading={reviewsCatalogListIsInProgress}
                        onConfirm={onSourceListChange}
                        options={sourcesOptions}
                        placeholder={getLocalizedString('REVIEWS__FILTER__SELECT_A_SOURCE')}
                        selectAllTitle={<Locale stringKey="REVIEWS__FILTER__ALL__SOURCES" />}
                        virtual={false}
                    />
                </Form.Item>
            </div>

            <div className={styles.ReviewsFilter_selectFilter}>
                <FormActiveMark
                    isVisible={
                        (Boolean(reviewFilterState[ReviewsFilterFieldNameEnum.dateFrom]) &&
                            Boolean(reviewFilterState[ReviewsFilterFieldNameEnum.dateTo])) ||
                        (Boolean(reviewFilterState[ReviewsFilterFieldNameEnum.collectionDateFrom]) &&
                            Boolean(reviewFilterState[ReviewsFilterFieldNameEnum.collectionDateTo])) ||
                        Boolean(periodPickerValue[0]) ||
                        Boolean(periodPickerValue[1])
                    }
                />
                <Form.Item label={<Locale stringKey="REVIEWS__FILTER__LABEL__PERIOD" />}>
                    <DatePicker.RangePicker
                        allowEmpty={[true, true]}
                        className={styles.ReviewsFilter_datepicker}
                        format="YYYY-MM-DD"
                        onCalendarChange={onPeriodCalendarChange}
                        onChange={
                            periodPickerActiveTab === 'collection' ? onCollectionDateRangeChange : onDataRangeChange
                        }
                        panelRender={renderPeriodPickerPanel}
                        placeholder={[getLocalizedString('TEXT__DATE_FROM'), getLocalizedString('TEXT__DATE_TO')]}
                        placement="topRight"
                        value={periodPickerValue}
                    />
                </Form.Item>
            </div>

            {hasDoctors && (
                <div className={styles.ReviewsFilter_selectFilter}>
                    <FormActiveMark isVisible={reviewFilterState[ReviewsFilterFieldNameEnum.doctorNames].length > 0} />
                    <Form.Item label={<Locale stringKey="REVIEWS__FILTER__LABEL__DOCTOR_NAME" />}>
                        <Select<Array<string>>
                            disabled={reviewsDoctorListIsInProgress}
                            dropdownStyle={reviewsFilterDropdownStyle}
                            getPopupContainer={reviewsFilterGetPopupContainer}
                            loading={reviewsDoctorListIsInProgress}
                            mode="multiple"
                            onChange={onDoctorsChange}
                            placeholder={<Locale stringKey="REVIEWS__FILTER__DOCTOR_NAME" />}
                            value={reviewFilterState[ReviewsFilterFieldNameEnum.doctorNames]}
                            virtual={false}
                        >
                            <Option value={DoctorsReviewsEnum.all}>
                                <Locale stringKey="REVIEWS__FILTER__ANY_DOCTOR" />
                            </Option>

                            {reviewsDoctorList?.results?.map(
                                (doctor: ReviewDoctorType): JSX.Element => (
                                    <Option key={doctor} value={doctor}>
                                        {doctor}
                                    </Option>
                                )
                            )}
                        </Select>
                    </Form.Item>
                </div>
            )}

            <div className={styles.ReviewsFilter_selectFilter}>
                <FormActiveMark
                    isVisible={
                        reviewFilterState[ReviewsFilterFieldNameEnum.processStatus].length > 0 ||
                        reviewFilterState[ReviewsFilterFieldNameEnum.customProcessStatus].length > 0
                    }
                />
                <Form.Item label={<Locale stringKey="REVIEWS__FILTER__LABEL__STATUS" />}>
                    <Select<Array<string>>
                        disabled={isReviewStatusListInProgress}
                        dropdownStyle={reviewsFilterDropdownStyle}
                        filterOption={handleFilterOption}
                        getPopupContainer={reviewsFilterGetPopupContainer}
                        loading={isReviewStatusListInProgress}
                        mode="multiple"
                        onChange={(value, option) => {
                            onStatusListChange(value, option);
                        }}
                        placeholder={<Locale stringKey="REVIEWS__FILTER__SELECT_A_STATUS" />}
                        value={[
                            ...reviewFilterState[ReviewsFilterFieldNameEnum.processStatus],
                            ...reviewFilterState[ReviewsFilterFieldNameEnum.customProcessStatus],
                        ]}
                        virtual={false}
                    >
                        {reviewStatusList?.statuses?.map((reviewStatus: ReviewStatusType): JSX.Element => {
                            const {statusCode, title} = reviewStatus;

                            return (
                                <Option key={statusCode} statusInfo={reviewStatus} value={statusCode}>
                                    {title}
                                </Option>
                            );
                        })}
                    </Select>
                </Form.Item>
            </div>

            <FormItemRadio<RepliesToReviewsEnum>
                className={styles.ReviewsFilter_selectFilter}
                fieldStateValue={reviewFilterState[ReviewsFilterFieldNameEnum.repliesToReviews]}
                form={form}
                label={<Locale stringKey="REVIEWS__FILTER__LABEL__REPLIES_TO_REVIEWS" />}
                name={ReviewsFilterFieldNameEnum.repliesToReviews}
                onClick={onRepliesToReviewsChange}
                options={REPLIES_TO_REVIEWS_OPTIONS}
            />

            <FormItemRadio<ContentOfReviewsEnum>
                className={styles.ReviewsFilter_selectFilter}
                fieldStateValue={reviewFilterState[ReviewsFilterFieldNameEnum.contentOfReviews]}
                form={form}
                label={<Locale stringKey="REVIEWS__FILTER__LABEL__CONTENT_OF_REVIEWS" />}
                name={ReviewsFilterFieldNameEnum.contentOfReviews}
                onClick={onContentOfReviewsChange}
                options={CONTENT_OF_REVIEWS_OPTIONS}
            />

            <FormItemRadio<PossibilityOfAnAnswerEnum>
                className={styles.ReviewsFilter_selectFilter}
                fieldStateValue={reviewFilterState[ReviewsFilterFieldNameEnum.possibilityOfAnAnswer]}
                form={form}
                label={<Locale stringKey="REVIEWS__FILTER__LABEL__POSSIBILITY_OF_AN_ANSWER" />}
                name={ReviewsFilterFieldNameEnum.possibilityOfAnAnswer}
                onClick={onPossibilityOfAnswerChange}
                options={POSSIBILITY_OF_AN_ANSWER_OPTIONS}
            />

            {showAppFilter && (
                <div className={styles.ReviewsFilter_selectFilter}>
                    <FormActiveMark isVisible={reviewFilterState[ReviewsFilterFieldNameEnum.locationIds].length > 0} />
                    <Form.Item label={<Locale stringKey="REVIEWS__FILTER__LABEL__APP_NAME" />}>
                        <Select<Array<string>>
                            disabled={reviewsAppListIsInProgress}
                            dropdownStyle={reviewsFilterDropdownStyle}
                            getPopupContainer={reviewsFilterGetPopupContainer}
                            loading={reviewsAppListIsInProgress}
                            mode="multiple"
                            onChange={onLocationIdsChange}
                            placeholder={<Locale stringKey="REVIEWS__FILTER__APP_NAME" />}
                            value={reviewFilterState[ReviewsFilterFieldNameEnum.locationIds]}
                            virtual={false}
                        >
                            {reviewsAppList?.results?.map(
                                (app: ReviewAppType): JSX.Element => (
                                    <Option key={app.id} value={app.id}>
                                        {app.name}
                                    </Option>
                                )
                            )}
                        </Select>
                    </Form.Item>
                </div>
            )}
        </>
    );
}
